import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { JournalStatus, RecalculateRequest, RecalculateReport, DateBasis } from "@shared";

// ═══════════════════════════════════════════════════════════
// Commission Hooks — TanStack Query wrappers for /api/commissions
// ═══════════════════════════════════════════════════════════

// ─── Response/Param Types ─────────────────────────────────

/** A flattened commission journal entry as returned by the API */
export interface CommissionEntry {
  id: number;
  entityId: number;
  entityTypeId: number;
  repId: number;
  ruleId: number;
  ruleVersion: number;
  period: string;
  status: JournalStatus;
  commissionType: string;
  commissionStructure: string;
  calculationBasis: string;
  baseAmount: number;
  rate: number;
  calculatedAmount: number;
  currency: string;
  originalAmount: number;
  convertedAmount: number;
  originalId: number;
  splitGroup: string;
  splitPercent: number;
  formulaSnapshot?: string | null;
  inputSnapshot?: string | null;
  repSnapshot?: string | null;
  approvalSnapshot?: string | null;
  productSnapshot?: string | null;
  tierDetail?: string | null;
  capApplied?: string | number | null;
  notes?: string | null;
  createdAt: string | number;
  fxSnapshot?: import("@shared").FxSnapshot | null;
  // Phase 2b: D1-canonical fields
  b24SnapshotId?: number | null;
  syncStatus?: string | null;
  paidAt?: number | null;
  approvedAt?: number | null;
  repName?: string | null;
  entityTitle?: string | null;
  ruleName?: string | null;
}

export interface CommissionListResponse {
  data: CommissionEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CommissionListParams {
  page?: number;
  pageSize?: number;
  period?: string;
  status?: JournalStatus;
  repIds?: number[];
  // Advanced filters
  structures?: string[];
  types?: string[];
  bases?: string[];
  currencies?: string[];
  amountMin?: number;
  amountMax?: number;
  dateFrom?: string; // ISO "YYYY-MM-DD"
  dateTo?: string; // ISO "YYYY-MM-DD"
  dateBasis?: DateBasis;
  capApplied?: boolean;
  hasClawback?: boolean;
  hasFx?: boolean;
}

export interface CommissionSummary {
  period: string;
  currency: string;
  totalPending: number;
  /** @deprecated Merged into totalPending (COM_PENDING covers PENDING+CALCULATED) */
  totalCalculated?: number;
  totalApproved: number;
  totalPaid: number;
  totalDisputed: number;
  totalClawback: number;
  totalVoid: number;
  pendingCount: number;
  /** @deprecated Merged into pendingCount */
  calculatedCount?: number;
  approvedCount: number;
  paidCount: number;
  disputedCount: number;
  clawbackCount: number;
  voidCount: number;
  netPayout: number;
  byRep: Array<{
    userId: number;
    repName?: string | null;
    calculated: number;
    approved: number;
    paid: number;
    count: number;
  }>;
}

export interface StatusTransitionResult {
  id: number;
  previousStatus: JournalStatus;
  newStatus: JournalStatus;
}

// ─── URL Builder ──────────────────────────────────────────

export function buildCommissionListUrl(params: CommissionListParams): string {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.period) qs.set("period", params.period);
  if (params.status) qs.set("status", params.status);
  if (params.repIds && params.repIds.length > 0) {
    qs.set("repIds", params.repIds.join(","));
  }
  if (params.structures && params.structures.length > 0)
    qs.set("structures", params.structures.join(","));
  if (params.types && params.types.length > 0) qs.set("types", params.types.join(","));
  if (params.bases && params.bases.length > 0) qs.set("bases", params.bases.join(","));
  if (params.currencies && params.currencies.length > 0)
    qs.set("currencies", params.currencies.join(","));
  if (params.amountMin != null) qs.set("amountMin", String(params.amountMin));
  if (params.amountMax != null) qs.set("amountMax", String(params.amountMax));
  if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
  if (params.dateTo) qs.set("dateTo", params.dateTo);
  if (params.dateBasis) qs.set("dateBasis", params.dateBasis);
  if (params.capApplied != null) qs.set("capApplied", params.capApplied ? "1" : "0");
  if (params.hasClawback != null) qs.set("hasClawback", params.hasClawback ? "1" : "0");
  if (params.hasFx != null) qs.set("hasFx", params.hasFx ? "1" : "0");
  const str = qs.toString();
  return `/api/commissions${str ? `?${str}` : ""}`;
}

// ─── List Commission Entries (paginated) ──────────────────
export function useCommissions(
  params: CommissionListParams = {},
  options?: Partial<UseQueryOptions<CommissionListResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.commissions.list(params as Record<string, unknown>),
    queryFn: () => api.raw<CommissionListResponse>(buildCommissionListUrl(params)),
    ...options,
  });
}

// ─── Single Commission Detail ─────────────────────────────
export function useCommission(id: number | undefined) {
  return useQuery({
    queryKey: queryKeys.commissions.detail(id!),
    queryFn: () => api.get<CommissionEntry>(`/api/commissions/${id}`),
    enabled: !!id,
  });
}

// ─── Commission Summary — by exact period OR by date range ────
export interface CommissionSummaryRangeParams {
  dateFrom: string; // ISO "YYYY-MM-DD"
  dateTo: string; // ISO "YYYY-MM-DD"
  dateBasis?: DateBasis;
}

export function useCommissionSummary(
  params: string | CommissionSummaryRangeParams | undefined,
  options?: Partial<UseQueryOptions<CommissionSummary>>,
) {
  const isRange = typeof params === "object";
  const qs = isRange
    ? new URLSearchParams({
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        ...(params.dateBasis ? { dateBasis: params.dateBasis } : {}),
      }).toString()
    : `period=${params ?? ""}`;

  return useQuery({
    queryKey: queryKeys.commissions.summary(params),
    queryFn: () => api.get<CommissionSummary>(`/api/commissions/summary?${qs}`),
    enabled: isRange ? !!params.dateFrom && !!params.dateTo : !!params,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// ─── Update Status (transition) ───────────────────────────
export function useUpdateCommissionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      notes,
      overrideAmount,
    }: {
      id: number;
      status: JournalStatus;
      notes?: string;
      overrideAmount?: number;
    }) =>
      api.put<StatusTransitionResult>(`/api/commissions/${id}/status`, {
        status,
        notes,
        overrideAmount,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

// ─── Recalculate Commissions ──────────────────────────────
export function useRecalculate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RecalculateRequest) =>
      api.post<RecalculateReport>("/api/commissions/recalculate", request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.commissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

// ═══════════════════════════════════════════════════════════
// Metrics Totals (cmn_totals EAV)
// ═══════════════════════════════════════════════════════════

export interface MetricTotalsRow {
  repId: number;
  period: string;
  metricKey: string;
  metricValue: number;
  ruleId: number;
  entityTypeId: number;
}

export interface MetricTotalsResponse {
  rows: MetricTotalsRow[];
}

export function useMetricsTotals(
  params: { repId: number; period: string; ruleId?: number },
  options?: Partial<UseQueryOptions<MetricTotalsResponse>>,
) {
  const { repId, period, ruleId } = params;
  return useQuery({
    queryKey: queryKeys.metrics.totals(repId, period, ruleId),
    queryFn: () => {
      const qs = new URLSearchParams({
        repId: String(repId),
        period,
        ...(ruleId != null ? { ruleId: String(ruleId) } : {}),
      });
      return api.get<MetricTotalsResponse>(`/api/admin/metrics/totals?${qs.toString()}`);
    },
    enabled: !!repId && !!period,
    ...options,
  });
}

// ═══════════════════════════════════════════════════════════
// Daily Snapshots
// ═══════════════════════════════════════════════════════════

export interface DailySummarySnapshot {
  memberId: string;
  summaryDate: string;
  period: string;
  statusPendingCount: number;
  statusPendingAmount: number;
  statusCalculatedCount: number;
  statusCalculatedAmount: number;
  statusApprovedCount: number;
  statusApprovedAmount: number;
  statusPaidCount: number;
  statusPaidAmount: number;
  statusDisputedCount: number;
  statusDisputedAmount: number;
  statusClawbackCount: number;
  statusClawbackAmount: number;
  statusVoidCount: number;
  statusVoidAmount: number;
  netPayout: number;
  totalEntries: number;
  topRepId: number | null;
  topRepAmount: number | null;
  topRep2Id: number | null;
  topRep2Amount: number | null;
  topRep3Id: number | null;
  topRep3Amount: number | null;
  activeRepCount: number;
  prevPeriodNetPayout: number | null;
  ruleCount: number;
  updatedAt?: number;
}

export function useSnapshotsByPeriod(
  period: string | undefined,
  options?: Partial<UseQueryOptions<DailySummarySnapshot[]>>,
) {
  return useQuery({
    queryKey: queryKeys.snapshots.byPeriod(period ?? ""),
    queryFn: () => api.get<DailySummarySnapshot[]>(`/api/admin/snapshots/period?period=${period}`),
    enabled: !!period,
    ...options,
  });
}

export function useSnapshot(
  date: string | undefined,
  options?: Partial<UseQueryOptions<DailySummarySnapshot>>,
) {
  return useQuery({
    queryKey: queryKeys.snapshots.byDate(date ?? ""),
    queryFn: () => api.get<DailySummarySnapshot>(`/api/admin/snapshots/${date}`),
    enabled: !!date,
    ...options,
  });
}
